import { OnInit, Component, DestroyRef, inject } from '@angular/core';
import { LoadPlanService } from '../proxy/operational/controllers';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  LoadPlanCreateDto,
  LoadPlanUpdateDto,
} from '../proxy/operational/dtos/load-plan';
import { ACTION_IDS, LOADPLAN_ACTIONS, LOADPLAN_CONFIG, LOADPLAN_FIELDS } from './load-plan.config';
import {
  BaseCrudComponent,
  createImportExcelModalConfig,
  CrudPage,
  ExcelImportErrorsExportLike,
  ExcelImportResultLike,
  GenericForm,
  IActionsSchema,
  ICrudService,
  IMPORT_MAX_SIZE_MB,
  ModalBuilder,
  PlanTypes,
  resolveActions,
  ZnExcelImportErrorsModal,
  ZnFileUploader,
} from '@zendra/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlanCreationService } from '../services/plan-creation.service';
import { LoadPlanPlanningOrchestratorService } from 'orchestrator';
import {
  LoadPlanProjectionDto,
  LoadPlanProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/load-plans/models';

const IMPORT_MODAL_TITLE = 'LanguageService::Action:ExcelImport.Title';
const IMPORT_AGAIN_LABEL = 'LanguageService::Action:ExcelImport.ImportAgain';
const IMPORT_TEMPLATE_FILE_NAME = 'LoadPlanImportTemplate.xlsx';

type LoadPlanImportResult = ExcelImportResultLike<unknown> & {
  fileName?: string;
};

@Component({
  selector: 'app-load-plan',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [ListService, LoadPlanService, ConfirmationService, PlanCreationService],
  templateUrl: './load-plan.html',
  styleUrl: './load-plan.scss',
})
export class LoadPlan extends BaseCrudComponent<
  LoadPlanProjectionDto,
  LoadPlanCreateDto,
  LoadPlanUpdateDto,
  LoadPlanProjectionExcelDownloadDto
> implements OnInit {
  private readonly loadPlanService = inject(LoadPlanService);
  private readonly planCreationService = inject(PlanCreationService);

  private readonly orchestratorService = inject(LoadPlanPlanningOrchestratorService);
  private readonly modalService = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);

  service: ICrudService<
    LoadPlanProjectionDto,
    LoadPlanCreateDto,
    LoadPlanUpdateDto,
    LoadPlanProjectionExcelDownloadDto
  > = {
    create: input => this.loadPlanService.create(input) as any,
    update: (id, input) => this.loadPlanService.update(id, input) as any,
    deleteByIds: ids => this.loadPlanService.deleteByIds(ids),
    getList: input => this.orchestratorService.getList(input),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'loadPlanId',
    ),
  };

  config = LOADPLAN_CONFIG;
  fields = LOADPLAN_FIELDS;
  actions: IActionsSchema | null = null;

  private readonly triggerMap: Record<number, () => void> = {
    [ACTION_IDS.importExcelModal]: () => this.openImportModal(),
    [ACTION_IDS.downloadExcelTemplateTrigger]: () =>
      this.downloadExcelTemplate(),
  };

  ngOnInit(): void {
    this.actions = resolveActions(
      LOADPLAN_ACTIONS,
      this.triggerMap,
    );
  }

  override create(): void {
    this.planCreationService.openCreate({
      planType: PlanTypes.Load,
      fields: this.fields,
      form: this.genericForm,
      injector: this.injector,
      destroyRef: this.destroyRef,
      title: `${this.localizationResource.LanguageService}::TableNewRegistry`,
      entityLocaleKey: this.config.localizationKey,
      onResolved: values => {
        this.selectedEntity = values;
      },
    });
  }

  protected prepareUpdateDto(data: any, selected: LoadPlanProjectionDto): LoadPlanUpdateDto {
    return {
      planVersion: data.planVersion,
      startDate: data.startDate,
      volume: data.volume,
      planTypeId: selected.planTypeId,
      harvestBlockId: data.harvestBlockId,
      versionId: data.versionId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'LoadPlan';
  }

  private openImportModal(): void {
    const modalRef = this.modalService.openModal(
      ZnFileUploader,
      createImportExcelModalConfig({
        component: ZnFileUploader,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.LoadPlan',
        inputs: {
          maxSizeMb: IMPORT_MAX_SIZE_MB,
          importFile: (formData: FormData) =>
            this.orchestratorService.importLoadPlan(formData),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: LoadPlanImportResult) => {
        if (result?.hasErrors) {
          this.openImportErrorsModal(result);
          return;
        }

        if (result) {
          this.refreshGrid();
        }
      });
  }

  private openImportErrorsModal(
    result: LoadPlanImportResult,
  ): void {
    const modalRef = this.modalService.openModal(
      ZnExcelImportErrorsModal,
      createImportExcelModalConfig({
        component: ZnExcelImportErrorsModal,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.LoadPlan',
        confirmLabel: IMPORT_AGAIN_LABEL,
        dismissLabel: 'LanguageService::Close',
        size: 'lg',
        inputs: {
          fileName: result.fileName ?? '',
          errors: result.errors,
          title: IMPORT_MODAL_TITLE,
          subTitle: 'Operational::Entity.LoadPlan',
          exportErrors: (
            input: ExcelImportErrorsExportLike,
          ) =>
            this.orchestratorService.exportLoadPlanImportErrors(
              input,
            ),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((modalResult?: { retry?: boolean }) => {
        if (modalResult?.retry) {
          this.openImportModal();
        }
      });
  }

  private downloadExcelTemplate(): void {
    this.orchestratorService
      .getLoadPlanImportTemplate()
      .subscribe({
        next: result => {
          this.toaster.success(
            'LanguageService::Actions:DownloadTemplateMessage',
            null,
            {
              life: 5000,
              messageLocalizationParams: [
                this.localizationService.instant(
                  'LanguageService::Actions:ExcelTemplateDownload',
                ),
              ],
            },
          );
          this.downloadFile(
            result,
            IMPORT_TEMPLATE_FILE_NAME,
          );
        },
        error: error => {
          this.toaster.error(error, null, {
            life: 5000,
          });
        },
      });
  }
}
